import { type FC, useMemo } from 'react';

import { Link } from '@vakantiesnl/components/src/atoms/Link';
import { TwoColumnTable } from '@vakantiesnl/components/src/molecules/TwoColumnTable';
import { useMicroCopyContext } from '@vakantiesnl/services/src/context/microCopyContext';
import { type MicroCopy, type Nbc, type RowContent, type Search } from '@vakantiesnl/types';

const NON_PREMIUM_CONTACTBLOCK_ITEMS = ['address'];

type Props = {
	accommodation: Search.FullAccommodationItem;
	isPremium?: boolean;
	showTitle?: boolean;
};

const mapContactDetails = (
	{ contactInformation, title }: Search.FullAccommodationItem,
	microCopies: MicroCopy,
	isPremium: boolean,
): RowContent[] => {
	/** For non premium acco's we only show contact details that are in NON_PREMIUM_CONTACTBLOCK_ITEMS */
	const contactInformationKeys = isPremium
		? Object.keys(contactInformation)
		: Object.keys(contactInformation).filter((key) => NON_PREMIUM_CONTACTBLOCK_ITEMS.includes(key));

	const socialLinksItem = contactInformationKeys.find((key) => key === 'socialLinks');
	if (socialLinksItem) {
		// putting socialLinks block as last
		contactInformationKeys.push(
			contactInformationKeys.splice(contactInformationKeys.indexOf(socialLinksItem), 1)[0],
		);
	}

	const mappedColumns: RowContent[] = [];

	contactInformationKeys.forEach((key) => {
		const rowTitle = microCopies[`contactInformation.${key}`];
		if (
			key === 'address' &&
			contactInformation.address &&
			contactInformation.address?.street &&
			contactInformation.address?.zipcode
		) {
			mappedColumns.push({
				rowTitle: rowTitle,
				cellContent: Object.keys(contactInformation.address)
					.map((addressKey) => contactInformation.address?.[addressKey as keyof Nbc.Address])
					.filter((addressValue) => addressValue && addressValue?.length > 0)
					.join(', 	')
					.trim(),
			});
		}

		if (key === 'email' && contactInformation?.email) {
			mappedColumns.push({
				rowTitle: rowTitle,
				cellContent: (
					<Link href={`mailto:${contactInformation[key]}`} rel="nofollow">
						{contactInformation[key]}
					</Link>
				),
			});
		}

		if (key === 'phone' && contactInformation?.phone) {
			mappedColumns.push({
				rowTitle: rowTitle,
				cellContent: <Link href={`tel:${contactInformation[key]}`}>{contactInformation[key]}</Link>,
			});
		}

		if (key === 'formattedUrl' && contactInformation?.formattedUrl) {
			mappedColumns.push({
				rowTitle: rowTitle,
				cellContent: (
					<Link href={`${contactInformation[key]}`} target="_blank" rel="nofollow">
						{title}
					</Link>
				),
			});
		}

		if (key === 'socialLinks' && contactInformation?.socialLinks && typeof contactInformation[key] === 'object') {
			mappedColumns.push({
				rowTitle: rowTitle,
				cellContent: Object.keys(contactInformation[key] as { [key: string]: string }).map((linkKey, i) => [
					i > 0 && ', ',
					<Link
						key={`${linkKey}-${i}`}
						href={(contactInformation[key] as { [key: string]: string })[linkKey]}
						rel="nofollow"
						target="_blank"
					>
						{microCopies[`contactInformation.${key}.${linkKey}`]}
					</Link>,
				]),
			});
		}
	});

	return mappedColumns;
};

export const AccommodationContactBlock: FC<Props> = ({ accommodation, isPremium = false, showTitle = true }) => {
	const microCopies = useMicroCopyContext();

	const mappedContactDetails = useMemo(
		(): RowContent[] => mapContactDetails(accommodation, microCopies, isPremium),
		[accommodation, microCopies, isPremium],
	);

	if (!mappedContactDetails.length) return null;

	return (
		<div data-cy={`contactDetails-${isPremium ? 'premium' : 'nonPremium'}`}>
			{mappedContactDetails && (
				<TwoColumnTable
					title={showTitle ? microCopies[`contactInformation.address`] : undefined}
					tableContent={mappedContactDetails}
					contentColumnRatio={1.4}
				/>
			)}
		</div>
	);
};
